// Review-and-acknowledge consent: the decisions the acknowledge screen and the
// sign-in gate make, kept pure so they can be tested without a browser.
//
// Auth hosts the acknowledge flow (issue #43), so the gate lives at the
// sign-in chokepoint rather than in each app. Apps still enforce server-side
// from the `consents_current` token claim — a gate that exists only in a client
// is not a gate.

// How the outstanding-documents load went. Tracked explicitly because "nothing
// outstanding" and "the load failed" are different facts that both leave the
// list empty — the same trap that hid a failed connected-accounts load (#41).
export const CONSENT_STATUS_LOADING = 'loading';
export const CONSENT_STATUS_LOADED = 'loaded';
export const CONSENT_STATUS_ERROR = 'error';

// What the acknowledge screen should show.
export const CONSENT_VIEW_ERROR = 'error';
export const CONSENT_VIEW_NONE = 'none';
export const CONSENT_VIEW_LIST = 'list';

export const CONSENT_PATH = '/consent';

/**
 * Which of the three acknowledge-screen states to render. A failed load must
 * show its error, so it is decided before the document list is consulted — an
 * empty list only means "nothing left to acknowledge" once the load succeeded.
 * @param {string} status one of the CONSENT_STATUS_* values
 * @param {Array} documents the outstanding documents
 * @returns {string} one of the CONSENT_VIEW_* values
 */
export function consentGateView(status, documents) {
    if (status === CONSENT_STATUS_ERROR) return CONSENT_VIEW_ERROR;
    return documents?.length ? CONSENT_VIEW_LIST : CONSENT_VIEW_NONE;
}

/**
 * Whether a just-issued access token says the user still owes an
 * acknowledgement, read from the `consents_current` claim.
 *
 * A token with no such claim reads as current. That is a deliberate rollout
 * choice, not a guess about unknown state: tokens minted before this feature
 * shipped carry no claim, and treating their absence as "outstanding" would
 * gate every signed-in user behind an acknowledge screen the moment this
 * deploys. Those tokens expire within the access-token lifetime and come back
 * with the claim. A token that cannot be parsed reads the same way — the server
 * is the authority either way, via GET /api/v1/consents/status.
 * @param {string} accessToken
 * @returns {boolean}
 */
export function consentsOutstandingFromToken(accessToken) {
    try {
        const payload = JSON.parse(atob(accessToken.split('.')[1]));
        if (typeof payload.consents_current !== 'boolean') return false;
        return !payload.consents_current;
    } catch {
        return false;
    }
}

/**
 * Whether publishing this key would be its first version.
 *
 * The publish form uses this to lock the "requires re-acknowledgement" choice
 * on: the server forces a first version to require re-consent (#48), because a
 * notice-only first version would leave the document with no consent floor and
 * so gate nobody. The UI should never offer a choice the server will override.
 * @param {string} key
 * @param {Array<{key: string}>} documents the tenant's current documents
 * @returns {boolean}
 */
export function isFirstVersionOfKey(key, documents) {
    if (!key) return true;
    return !(documents || []).some((document) => document.key === key);
}
